import { faPen, faTrash } from '@fortawesome/pro-regular-svg-icons';
import {
    faPen as faPenSolid,
    faTrash as faTrashSolid,
} from '@fortawesome/pro-solid-svg-icons';
import {
    IPhysicalSensorModelSdResponse,
    SensorStandard,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { ArrowLink } from 'components/link/ArrowLink';
import { ActionMenuTableCell } from 'components/menu/ActionMenuTableCell';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { ThirdPartySensorModelsPagedDataSource } from 'custom_hooks/data_hooks/data_source/thirdPartySensorModelsDataSource';
import { ApprovedSensorBadge } from 'pages/Settings/ThirdPartySensorModels/ApprovedSensorBadge';
import { EditThirdPartySensorModal } from 'pages/Settings/ThirdPartySensorModels/EditThirdPartySensorModal';
import { useMemo, useState } from 'react';
import { DebugValue } from 'shared/debug/DebugValue';
import { excludeNullAndUndefined } from 'utils/array';
import { getId } from 'utils/data';
import { Lookup } from 'utils/types';

export const SensorStandardFormatLookup: Lookup<SensorStandard, string> = {
    [SensorStandard.Analog]: 'Analog',
    [SensorStandard.Iepe]: 'IEPE',
};

export type ActionInfo = Readonly<{
    type: 'edit' | 'delete';
    item: IPhysicalSensorModelSdResponse;
}>;

export type ThirdPartySensorsTableProps = Readonly<{
    dataSource: ThirdPartySensorModelsPagedDataSource;
    isApproved: boolean;
    className?: string;
}>;

export function ThirdPartySensorsTable({
    dataSource,
    isApproved,
}: ThirdPartySensorsTableProps) {
    const { showToast } = useNotificationContext();
    const [action, setAction] = useState<ActionInfo>();

    const { reloadPage } = dataSource;

    const { mutate: deleteSensorModel, isPending: isDeleting } = useMutation({
        mutationFn: async () => {
            if (action?.item.Id !== undefined) {
                await sdClient.deletePhysicalSensorModel({
                    physicalSensorModelId: action.item.Id,
                });
            }
        },

        onSuccess: () => {
            setAction(undefined);
            reloadPage();
            showToast({
                content: 'Successfully deleted third party sensor model',
                type: 'success',
            });
        },

        onError: () => {
            showToast({
                content: 'Failed to delete third party sensor model',
                type: 'error',
            });
        },
    });

    const onEditClose = (updated?: boolean) => {
        if (updated) {
            reloadPage();
        }

        setAction(undefined);
    };

    const columns = useMemo<TableColumn<IPhysicalSensorModelSdResponse>[]>(
        () =>
            excludeNullAndUndefined([
                {
                    header: () => 'Manufacturer',
                    id: 'manufacturer',
                    value: ({ rowData }) => (
                        <div className="text-nowrap">
                            {rowData.Manufacturer.Name}{' '}
                            {rowData.Manufacturer.CorporationId ? undefined : (
                                <ApprovedSensorBadge />
                            )}
                            <DebugValue
                                description="Third-Party Sensor"
                                value={rowData}
                            />
                        </div>
                    ),
                    primary: true,
                    locked: true,
                    sortOn: 'Manufacturer.Name',
                },
                {
                    header: () => 'Model Number',
                    id: 'model-number',
                    value: ({ rowData }) => (
                        <div className="text-nowrap">
                            <ArrowLink
                                title="Go to details"
                                to={`/settings/thirdPartySensors/details/${rowData.Id}`}
                            >
                                {rowData.ModelNumber}
                            </ArrowLink>
                        </div>
                    ),
                    sortOn: 'ModelNumber',
                },
                {
                    header: () => 'Standard',
                    id: 'standard',
                    value: ({ rowData }) => (
                        <div className="text-nowrap">
                            {SensorStandardFormatLookup[rowData.SensorStandard]}
                        </div>
                    ),
                },
                {
                    header: () => 'Configurations',
                    id: 'configurations',
                    value: ({ rowData }) => (
                        <div className="text-nowrap">
                            {rowData.OutputSensorDataConfigCount}
                        </div>
                    ),
                },
                !isApproved
                    ? {
                          header: () => 'Corporation',
                          id: 'corporation',
                          value: ({ rowData }) => (
                              <div className="text-nowrap">
                                  {rowData.Corporation?.CorporationName}
                              </div>
                          ),
                          sortOn: 'Corporation.CorporationName',
                      }
                    : undefined,
                {
                    header: () => 'Notes',
                    id: 'notes',
                    value: ({ rowData }) => (
                        <div className="word-wrap-anywhere ellipsis-2-lines">
                            {rowData.Notes}
                        </div>
                    ),
                },
                isApproved
                    ? {
                          header: () => 'Actions',
                          id: 'actions',
                          locked: true,
                          value: ({ rowData }) => (
                              <ActionMenuTableCell>
                                  <HoverIconButton
                                      title="Edit Sensor Model"
                                      icon={faPen}
                                      hoverIcon={faPenSolid}
                                      color="var(--icon-action-active)"
                                      hoverColor="var(--icon-action-hover)"
                                      size="14"
                                      onClick={() =>
                                          setAction({ type: 'edit', item: rowData })
                                      }
                                  />
                                  <HoverIconButton
                                      title="Delete Sensor Model"
                                      icon={faTrash}
                                      hoverIcon={faTrashSolid}
                                      color="var(--icon-action-active)"
                                      hoverColor="var(--error-default)"
                                      onClick={() =>
                                          setAction({ type: 'delete', item: rowData })
                                      }
                                      disabled={isDeleting}
                                  />
                              </ActionMenuTableCell>
                          ),
                      }
                    : undefined,
            ]),
        [isApproved, isDeleting]
    );

    return (
        <>
            <DataSourceTable
                dataSource={dataSource}
                rowKey={getId}
                columns={columns}
                noResultMessage="No sensor models found."
            />
            {action?.type === 'edit' && (
                <EditThirdPartySensorModal
                    physicalSensorModel={action.item}
                    onClose={onEditClose}
                />
            )}
            {action?.type === 'delete' && (
                <ConfirmModal
                    title="Delete Sensor Model"
                    actionText="delete this third party sensor model?"
                    checkText="delete this third party sensor model?"
                    actionButtonText="Delete"
                    closable={!isDeleting}
                    actionable={!isDeleting}
                    onAction={deleteSensorModel}
                    onClose={() => setAction(undefined)}
                />
            )}
        </>
    );
}
